import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBarBySlug, createUser } from '../services/userService';
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Container,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import lastCallLogo from '../assets/logo.png';
import { COLORS } from '../constants/colors';
import VerificationModal from '../components/VerificationModal';
import { api } from '../utils/api';
import ReactMarkdown from 'react-markdown';

export default function SignUpForm() {
    const { barSlug } = useParams();
    const [barInfo, setBarInfo] = useState(null);
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [content, setContent] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        email: '',
        gender: '',
        birthDate: null,
        consent: false,
        membershipStatus: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [fetchAttempted, setFetchAttempted] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [birthdateError, setBirthdateError] = useState('');
    const [showVerification, setShowVerification] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [pendingUserData, setPendingUserData] = useState(null);


    // Add a separate state for verification errors
    const [verificationError, setVerificationError] = useState('');

    useEffect(() => {
        let isMounted = true;

        const fetchBarInfo = async () => {
            if (!barSlug || fetchAttempted) return;

            try {
                const barData = await getBarBySlug(barSlug);

                if (!isMounted) return;

                if (barData) {
                    setBarInfo(barData);
                    // Fetch signup content
                    try {
                        const response = await fetch(`${process.env.REACT_APP_URL_backend}/api/users/signup-content/${barData.id}`);
                        if (response.ok) {
                            const contentData = await response.json();
                            if (contentData.success) {
                                setTitle(contentData.data.title);
                                setSubtitle(contentData.data.subtitle);
                                setContent(contentData.data.content);
                            }
                        }
                    } catch (contentError) {
                        console.error('Error fetching signup content:', contentError);
                    }

                    // If membership question is not included, reset membershipStatus
                    if (!barData.includeMembershipQuestion) {
                        setFormData(prev => ({
                            ...prev,
                            membershipStatus: ''
                        }));
                    }
                } else {
                    setError('Bar not found');
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Error fetching bar info:', err);
                    setError('Failed to load bar information');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                    setFetchAttempted(true);
                }
            }
        };

        fetchBarInfo();

        return () => {
            isMounted = false;
        };
    }, [barSlug, fetchAttempted]);

    // Function to check if user is 21 or older
    const isOver21 = (birthDate) => {
        if (!birthDate) {
            return false;
        }
        const today = dayjs();
        const birth = dayjs(birthDate);
        const isOldEnough = today.diff(birth, 'year') >= 21;
        return isOldEnough;
    };

    // Enhanced phone number formatting and validation
    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const numbers = value.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
        return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    const isValidPhoneNumber = (phone) => {
        // Remove all non-digits and check if exactly 10 digits
        const digitsOnly = phone.replace(/\D/g, '');
        return digitsOnly.length === 10;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        if (formatted.length <= 14) { // (XXX) XXX-XXXX
            setFormData({ ...formData, phoneNumber: formatted });

            // Clear error if valid, show error if complete but invalid
            if (formatted.length === 14) {
                if (isValidPhoneNumber(formatted)) {
                    setPhoneError('');
                } else {
                    setPhoneError('Please enter a valid phone number');
                }
            } else if (formatted.length > 0) {
                setPhoneError('Please enter a complete phone number');
            } else {
                setPhoneError('');
            }
        }
    };

    // Add email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Add email change handler
    const handleEmailChange = (e) => {
        const email = e.target.value;
        setFormData({ ...formData, email: email });

        if (email.length > 0) {
            if (isValidEmail(email)) {
                setEmailError('');
            } else {
                setEmailError('Please enter a valid email address');
            }
        } else {
            setEmailError('');
        }
    };

    const handleBirthdateChange = (newValue) => {
        setFormData({ ...formData, birthDate: newValue });

        if (newValue) {
            if (isOver21(newValue)) {
                setBirthdateError('');
            } else {
                setBirthdateError('You must be 21 or older to join');
            }
        } else {
            setBirthdateError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Add email validation to submit handler
        if (!isValidEmail(formData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Validate phone number first
        if (!isValidPhoneNumber(formData.phoneNumber)) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        if (!formData.consent) {
            setError('Please agree to receive text messages to continue.');
            return;
        }
        if (!isOver21(formData.birthDate)) {
            setError('You must be 21 or older to join.');
            return;
        }
        if (barInfo?.includeMembershipQuestion && !formData.membershipStatus) {
            setError('Please select your membership status.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Format phone number to +1XXXXXXXXXX format
            const formattedPhone = `+1${formData.phoneNumber.replace(/\D/g, '')}`;

            const userData = {
                name: formData.name,
                phoneNumber: formattedPhone,
                email: formData.email,
                gender: formData.gender || '',
                birthdate: formData.birthDate.toISOString(),
                consent: formData.consent,
                accountID: barInfo.id,
                membershipStatus: formData.membershipStatus
            };

            // Get params in case of referral
            const urlParams = new URLSearchParams(window.location.search);
            const campaign = urlParams.get('campaign');
            const ref = urlParams.get('ref');

            // Check if this is a referred signup
            if (ref) {
                // Initiate phone verification for referred signups
                try {
                    const verificationResponse = await api.post('/api/verify/initiate', {
                        phone: formattedPhone
                    });

                    if (verificationResponse.success) {
                        // Store user data for after verification
                        setPendingUserData({ userData, campaign, ref });
                        setShowVerification(true);
                        setLoading(false);
                        return;
                    } else {
                        throw new Error('Failed to send verification code');
                    }
                } catch (verifyError) {
                    console.error('Verification initiation error:', verifyError);
                    setError('Failed to send verification code. Please try again.');
                    setLoading(false);
                    return;
                }
            } else {
                // Direct signup without verification
                // barInfo.id = 'Mk0CIfeQ3tSgQKnvWJEy'; // TODO GET RID OF THIS
                await createUser(barInfo.id, userData, campaign, ref);
                setSuccess(true);
                setFormData({
                    name: '',
                    phoneNumber: '',
                    email: '',
                    gender: '',
                    birthDate: null,
                    consent: false,
                    membershipStatus: ''
                });
            }
        } catch (err) {
            setError('Failed to submit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (!pendingUserData) return;

        try {
            const verificationResponse = await api.post('/api/verify/initiate', {
                phone: pendingUserData.userData.phoneNumber
            });

            if (!verificationResponse.success) {
                throw new Error('Failed to send verification code');
            }

            // Clear any existing verification errors
            setVerificationError('');
        } catch (error) {
            console.error('Resend verification error:', error);
            throw error; // Re-throw so the modal can handle it
        }
    };

    const handleVerification = async (code) => {
        if (!pendingUserData) return;

        setVerificationLoading(true);
        setVerificationError(''); // Clear verification error

        try {

            // Verify the code
            const verifyResponse = await api.post('/api/verify', {
                phone: pendingUserData.userData.phoneNumber,
                code: code
            });

            if (verifyResponse.success) {
                // Verification successful, now create the user
                // barInfo.id = 'Mk0CIfeQ3tSgQKnvWJEy'; // TODO GET RID OF THIS
                await createUser(
                    barInfo.id,
                    pendingUserData.userData,
                    pendingUserData.campaign,
                    pendingUserData.ref
                );

                setSuccess(true);
                setShowVerification(false);
                setPendingUserData(null);
                setVerificationError('');
                setFormData({
                    name: '',
                    phoneNumber: '',
                    email: '',
                    gender: '',
                    birthDate: null,
                    consent: false,
                    membershipStatus: ''
                });
            } else {
                // Set verification error - this will trigger auto-resend in modal
                setVerificationError(verifyResponse.error || 'Invalid verification code');
            }
        } catch (err) {
            console.error('Verification error caught:', err);

            // Check if it's a structured error from the API with verification details
            if (err.data && err.data.error) {
                setVerificationError(err.data.error);
            } else if (err.message) {
                setVerificationError(err.message);
            } else {
                setVerificationError('Invalid verification code');
            }
        } finally {
            setVerificationLoading(false);
        }
    };

    const handleCloseVerification = () => {
        setShowVerification(false);
        setPendingUserData(null);
        setVerificationError(''); // Clear verification error
        setError('');
    };

    if (!barSlug) return <Typography>No bar specified</Typography>;
    if (loading) return <Typography>Loading...</Typography>;
    if (error) return (
        <Container>
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                {error}
            </Typography>
        </Container>
    );

    // Update the textFieldStyle to be more comprehensive
    const textFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px'
        },
        '& .MuiSelect-outlined': {
            borderRadius: '12px'
        },
        // For DatePicker
        '& .MuiInputBase-root': {
            borderRadius: '12px'
        }
    };

    return (
        <Container
            maxWidth={false}
            disableGutters
            sx={{
                backgroundColor: COLORS.PURPLE.hex,
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 0,
                py: 0
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mb: 0,
                mt: 0,
                px: 2
            }}>
                <Link
                    to="/"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        backgroundColor: 'transparent',
                        padding: '12px',
                        transition: 'transform 0.2s',
                    }}
                >
                    <img
                        src={lastCallLogo}
                        alt="LastCall Logo"
                        style={{
                            height: '125px',
                            width: 'auto',
                            borderRadius: '8px',
                            objectFit: 'contain'
                        }}
                    />
                </Link>
            </Box>

            <Paper
                elevation={6}
                sx={{
                    p: 4,
                    mt: 0,
                    mb: 4,
                    borderRadius: '16px',
                    width: '90%',
                    maxWidth: '600px',
                    backgroundColor: 'white',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                }}
            >
                {success ? (
                    <ReactMarkdown
                        components={{
                            p: ({ children }) => (
                                <Typography variant="h5" align="center" paragraph>
                                    {children}
                                </Typography>
                            ),
                            a: ({ href, children }) => (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    {children}
                                </a>
                            )
                        }}
                    >
                        {content || `Thanks for joining! Get ready to start receiving exclusive deals from ${barInfo.barName}.

Leave a review below and show your bartender to claim your free drink!

[Click here to leave a review!](https://g.co/kgs/UnzeTST)`}
                    </ReactMarkdown>
                ) : (
                    <>
                        <Typography
                            variant="h4"
                            component="h2"
                            gutterBottom
                            align="center"
                            className="inter-custom"
                            sx={{
                                mb: 4,
                                fontWeight: 600,
                                borderRadius: '12px',
                                color: '#3d3d3d'
                            }}
                        >
                            {title || `Sign up for ${barInfo?.barName}`}
                        </Typography>
                        <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            align="center"
                            className="inter-custom"
                            sx={{
                                mb: 4,
                                borderRadius: '12px',
                                color: '#3d3d3d'
                            }}
                        >
                            {subtitle || 'Stay in the know! Drink deals, events, and more straight to your phone'}
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                margin="normal"
                                className="inter-custom"
                                sx={textFieldStyle}
                            />
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={formData.phoneNumber}
                                onChange={handlePhoneChange}
                                margin="normal"
                                required
                                className="inter-custom"
                                sx={textFieldStyle}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                                }}
                                placeholder="(555) 555-5555"
                                error={!!phoneError}
                                helperText={phoneError}
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                type="text"
                                value={formData.email}
                                onChange={handleEmailChange}
                                margin="normal"
                                required
                                className="inter-custom"
                                sx={textFieldStyle}
                                error={!!emailError}
                                helperText={emailError}
                            />
                            <FormControl fullWidth margin="normal" sx={textFieldStyle}>
                                <InputLabel className="inter-custom">Gender (Optional)</InputLabel>
                                <Select
                                    value={formData.gender}
                                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                    label="Gender (Optional)"
                                    className="inter-custom"
                                >
                                    <MenuItem value="male">Man</MenuItem>
                                    <MenuItem value="female">Woman</MenuItem>
                                    <MenuItem value="nonBinary">Non-binary</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                    <MenuItem value="">Prefer not to say</MenuItem>
                                </Select>
                            </FormControl>

                            {barInfo?.includeMembershipQuestion && (
                                <FormControl fullWidth margin="normal" required sx={textFieldStyle}>
                                    <InputLabel className="inter-custom">Are you a member?</InputLabel>
                                    <Select
                                        value={formData.membershipStatus}
                                        onChange={(e) => setFormData({ ...formData, membershipStatus: e.target.value })}
                                        label="Are you a member?"
                                        className="inter-custom"
                                        required
                                    >
                                        <MenuItem value="yes">Yes</MenuItem>
                                        <MenuItem value="no">No</MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Birth Date"
                                    value={formData.birthDate}
                                    onChange={handleBirthdateChange}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            margin: "normal",
                                            required: true,
                                            error: !!birthdateError,
                                            helperText: birthdateError || "Must be 21 or older",
                                            className: "inter-custom",
                                            sx: textFieldStyle
                                        }
                                    }}
                                    disableFuture
                                />
                            </LocalizationProvider>

                            <Box sx={{ mt: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.consent}
                                            onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                                            required
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color="text.secondary">
                                            CONSENT TO RECEIVE TEXT MESSAGES: By submitting this form and signing up for texts,
                                            you consent to receive promotional messages from {barInfo.barName} at the number provided and from LastCall,
                                            including messages sent by autodialer. Consent is not a condition of purchase.
                                            Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by
                                            replying STOP. Reply HELP for help.
                                        </Typography>
                                    }
                                />
                            </Box>

                            {error && (
                                <Typography color="error" sx={{ mt: 2 }}>
                                    {error}
                                </Typography>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                disabled={
                                    loading ||
                                    !isOver21(formData.birthDate) ||
                                    !isValidPhoneNumber(formData.phoneNumber) ||
                                    !isValidEmail(formData.email)
                                }
                                className="inter-custom"
                                sx={{
                                    mt: 3,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    py: 1.5
                                }}
                                fullWidth
                                size="large"
                            >
                                {loading ? 'Submitting...' : 'Join Now'}
                            </Button>

                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Link
                                    to="/privacy-policy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Privacy Policy
                                    </Typography>
                                </Link>
                                <Link
                                    to="/terms-and-conditions"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        Terms and Conditions
                                    </Typography>
                                </Link>
                            </Box>
                        </form>
                    </>
                )}
            </Paper>

            {/* Update the verification modal to include the error prop */}
            <VerificationModal
                open={showVerification}
                onClose={handleCloseVerification}
                onVerify={handleVerification}
                onResendCode={handleResendCode}
                phoneNumber={formData.phoneNumber}
                loading={verificationLoading}
                verificationError={verificationError}
            />
        </Container>
    );
}